import { useQuery } from "@tanstack/react-query";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type SourceMeta } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  BarList,
  EmptyState,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

function SourceRight({ meta }: { meta?: SourceMeta }) {
  if (!meta?.available) return null;
  return (
    <span className="flex items-center gap-2">
      <StatusPill status={meta.status ?? "unknown"} />
      <span className="text-xs text-slate-500">{fmtRelative(meta.captured_at)}</span>
    </span>
  );
}

export default function CatalogueHealthPage() {
  const q = useQuery({ queryKey: ["catalogue-health"], queryFn: dashApi.catalogueHealth });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalogue Health"
        description="PIM2 enrichment, supplier feeds, import quality, publishing and data hygiene. No product text or media is read — completeness checks only."
        right={<SourceRight meta={d?.source} />}
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d || !d.source.available ? (
        <EmptyState>No PIM snapshot has been captured yet.</EmptyState>
      ) : (
        <>
          {d.chronic_feeds.length > 0 && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
              <span className="font-semibold">Chronic feed failures:</span>{" "}
              {d.chronic_feeds.map((f) => `${f.name} (${f.failures_7d} failures/7d)`).join(", ")} — supplier pricing/stock data is stale.
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Active products" value={d.headline.products_active} unit="count" />
            <KpiCard label="Publishable" value={d.headline.products_publishable} unit="count" />
            <KpiCard label="New products 7d" value={d.headline.products_new_7d} unit="count" />
            <KpiCard label="Failed imports (range)" value={d.headline.failed_imports} unit="count" tone={d.headline.failed_imports ? "danger" : undefined} />
            <KpiCard label="Orphaned running jobs" value={d.orphaned_jobs} unit="count" tone={d.orphaned_jobs ? "danger" : undefined} />
            <KpiCard label="Duplicates pending" value={d.duplicates?.pending_total ?? null} unit="count" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="Enrichment — publishable set"
              subtitle={d.enrichment ? `${d.enrichment.publishable_total} product(s) currently flagged for the storefront` : undefined}
            >
              {d.enrichment ? (
                <BarList
                  rows={[
                    { label: "Missing EN description", value: d.enrichment.no_description_en },
                    { label: "Missing ES description", value: d.enrichment.no_description_es },
                    { label: "Missing meta title", value: d.enrichment.no_meta_title },
                    { label: "Missing image (publish blocker)", value: d.enrichment.no_image },
                  ]}
                  unit="count"
                />
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel
              title="Catalogue identifier gaps — active set"
              subtitle={d.catalog_gaps ? `${d.catalog_gaps.active_total.toLocaleString()} active product(s)` : undefined}
            >
              {d.catalog_gaps ? (
                <BarList
                  rows={[
                    { label: "No EAN", value: d.catalog_gaps.no_ean },
                    { label: "No brand", value: d.catalog_gaps.no_brand },
                    { label: "No category", value: d.catalog_gaps.no_category },
                  ]}
                  unit="count"
                />
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Supplier feed freshness" subtitle="Hours since last successful import per active feed.">
              {d.feed_freshness.length ? (
                <ul className="space-y-2 text-sm">
                  {d.feed_freshness.map((f) => (
                    <li key={f.name} className="flex items-center justify-between">
                      <span className="text-slate-300">
                        {f.name}
                        {!f.schedule_enabled && <span className="ml-2 text-xs text-slate-500">(schedule off)</span>}
                      </span>
                      <span className={f.hours_since_success === null ? "text-rose-300" : f.hours_since_success > 48 ? "text-amber-300" : "text-slate-200"}>
                        {f.hours_since_success === null ? "never succeeded" : `${Math.round(f.hours_since_success)}h ago`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState compact>No active feeds.</EmptyState>
              )}
            </Panel>
            <Panel
              title="Import quality (range)"
              subtitle={d.import_quality ? `${d.import_quality.jobs} job(s) · ${d.import_quality.unchanged_files} unchanged file(s)` : undefined}
            >
              {d.import_quality ? (
                <BarList
                  rows={[
                    { label: "Matched by EAN", value: d.import_quality.matched_by_ean },
                    { label: "Matched by MPN", value: d.import_quality.matched_by_mpn },
                    { label: "Matched by SKU", value: d.import_quality.matched_by_sku },
                    { label: "Unresolved", value: d.import_quality.unresolved },
                    { label: "Errors", value: d.import_quality.errors },
                    { label: "Price changes", value: d.import_quality.price_changes },
                    { label: "Stock changes", value: d.import_quality.stock_changes },
                  ]}
                  unit="count"
                />
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel
              title="Publishing funnel"
              subtitle={d.publishing ? `PS queue idle ${d.publishing.ps_queue_idle_days ?? "—"} day(s) · ${d.publishing.ps_terminal_failures} terminal failure(s)` : undefined}
            >
              {d.publishing ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Flagged for PrestaShop</dt><dd className="font-medium text-slate-200">{d.publishing.ps_flagged}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Flagged for Odoo</dt><dd className="font-medium text-slate-200">{d.publishing.odoo_flagged.toLocaleString()}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">PS mappings (historical)</dt><dd className="font-medium text-slate-200">{d.publishing.ps_mappings.toLocaleString()}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Duplicate backlog">
              <BarList rows={d.duplicates?.by_confidence ?? []} unit="count" empty="No pending duplicate groups." />
              <p className="mt-2 text-xs text-slate-500">Flagged pending review — not confirmed duplicates.</p>
            </Panel>
            <Panel
              title="Used-device resale queue"
              subtitle={d.resale_queue ? `Oldest pending ${d.resale_queue.oldest_pending_days} day(s) · ${d.resale_queue.published_total} published` : undefined}
            >
              <BarList rows={d.resale_queue?.by_status ?? []} unit="count" empty="No resale intakes yet." />
            </Panel>
          </div>

          <Panel title="PIM admin activity (24h)" subtitle="Audit-event metadata only — payloads are never read.">
            {d.audit_activity ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <KpiCard label="Events" value={d.audit_activity.events_24h} unit="count" />
                <KpiCard label="Warnings / failures" value={d.audit_activity.notable_24h} unit="count" tone={d.audit_activity.notable_24h ? "danger" : undefined} />
              </div>
            ) : (
              <EmptyState compact>Not available in this snapshot.</EmptyState>
            )}
          </Panel>

          <GapList gaps={d.source.gaps} />
          <FeedbackWidget targetType="page" targetRef="/pim/catalogue-health" />
        </>
      )}
    </div>
  );
}
